import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaSignInAlt, FaTimes, FaMapMarkerAlt } from "react-icons/fa";
import api from "../../../libs/axios";
import { BrowserLocation, getBrowserLocation } from "../../../utils/geolocation.utils";

/**
 * Check in to a visit: where the engineer is, and optionally the address in
 * words. Nothing else.
 *
 * The verification media — the visit verification photo and the safety photos
 * and videos — is NOT part of check-in. It is submitted beforehand, with its own
 * location, from Photos & Files on the visit (POST /inc/visits/:id/verification),
 * and on a project that requires it check-in stays locked until that is
 * approved. By the time this dialog opens there is nothing left to upload.
 */

interface VisitCheckInModalProps {
  visitId: number;
  visitNumber: number;
  // True on a project that requires verification approval before check-in —
  // only changes the wording: the dialog can then say it was approved.
  verificationRequired?: boolean;
  open: boolean;
  onClose: () => void;
  // Called after a successful check-in so the tab can reload the visit.
  onDone: () => void;
}

const VisitCheckInModal: React.FC<VisitCheckInModalProps> = ({
  visitId,
  visitNumber,
  verificationRequired = false,
  open,
  onClose,
  onDone,
}) => {
  const [coords, setCoords] = useState<BrowserLocation | null>(null);
  const [geoError, setGeoError] = useState<string>("");
  const [locating, setLocating] = useState(false);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  // Ask for the fix as soon as the dialog opens: the API rejects a check-in
  // without valid geo, so there is no point waiting until the user hits Submit
  // to find out the browser denied permission.
  const locate = async () => {
    setLocating(true);
    setGeoError("");
    try {
      setCoords(await getBrowserLocation("started"));
    } catch (error: any) {
      setGeoError(error?.message || "Could not get a location fix.");
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setAddress("");
    setCoords(null);
    setGeoError("");
    locate();
    // locate is stable enough for this — it only closes over setState setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!coords) {
      toast.error("A location fix is required to start a visit");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/inc/visits/${visitId}/check-in`, {
        latitude: coords.latitude,
        longitude: coords.longitude,
        address: address.trim() || undefined,
      });
      if (res.data?.data?.within_geofence === false) {
        toast("Checked in outside the 500m geo-fence", { icon: "⚠️" });
      }
      onDone();
      onClose();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="bg-white rounded shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FaSignInAlt className="w-4 h-4 text-green-700" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Check in to Visit #{visitNumber}</h3>
              <p className="text-xs text-gray-500">
                {verificationRequired
                  ? "Verification is approved — only your location is recorded now."
                  : "Your location is recorded against this visit."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="p-2 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            <FaTimes className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Location — mandatory, so it is shown resolved rather than assumed. */}
          <div className="border border-gray-200 rounded p-3">
            <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
              <FaMapMarkerAlt className="w-3 h-3 text-blue-700" /> Location
            </div>
            {locating ? (
              <p className="text-sm text-gray-500">Getting your location…</p>
            ) : coords ? (
              <div className="text-sm text-gray-800">
                <span className="font-mono">
                  {coords.latitude.toFixed(6)}, {coords.longitude.toFixed(6)}
                </span>
                {coords.accuracy ? (
                  <span className="text-xs text-gray-400"> · ±{Math.round(coords.accuracy)}m</span>
                ) : null}
                <button onClick={locate} className="ml-2 text-xs text-blue-700 hover:underline">
                  Refresh
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-red-700">{geoError || "No location yet."}</p>
                <button onClick={locate} className="mt-1 text-xs text-blue-700 hover:underline">
                  Try again
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Address (optional)</label>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Where you are, in words"
              maxLength={1000}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
            />
          </div>

          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
            The visit verification photo and the safety photos and videos are not taken here — submit them under
            Photos &amp; Files on the visit.
          </p>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-100 font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !coords}
            title={!coords ? "A location fix is required to start a visit" : undefined}
            className="flex-1 px-4 py-2 text-sm bg-green-700 text-white rounded hover:bg-green-800 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {busy ? "Checking in…" : "Check in"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitCheckInModal;
